// Enhanced cache service with smart invalidation and performance optimizations
class EnhancedCacheService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default TTL
    this.maxCacheSize = 100; // Maximum number of cache entries
    this.performanceMetrics = new Map();
  }

  // Set cache with TTL and performance tracking
  set(key, data, ttl = this.defaultTTL) {
    const timestamp = Date.now();
    
    // Clean up old entries if cache is full
    if (this.cache.size >= this.maxCacheSize) {
      this.cleanupOldEntries();
    }
    
    this.cache.set(key, data);
    this.timestamps.set(key, timestamp + ttl);
    
    // Track cache hit performance
    this.performanceMetrics.set(key, {
      lastAccessed: timestamp,
      accessCount: 0,
      dataSize: JSON.stringify(data).length
    });
  }

  // Get cache with performance tracking
  get(key) {
    const expiry = this.timestamps.get(key);
    if (!expiry || Date.now() > expiry) {
      this.delete(key);
      return null;
    }
    
    // Update performance metrics
    const metrics = this.performanceMetrics.get(key);
    if (metrics) {
      metrics.lastAccessed = Date.now();
      metrics.accessCount++;
    }
    
    return this.cache.get(key);
  }

  // Smart cache invalidation based on patterns
  invalidatePattern(pattern) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.delete(key));
  }

  // Invalidate all order-related caches
  invalidateOrders() {
    this.invalidatePattern('order');
    this.invalidatePattern('picklist');
    this.invalidatePattern('processing');
  }

  // Clean up old entries based on access patterns
  cleanupOldEntries() {
    // const now = Date.now();
    const entries = Array.from(this.performanceMetrics.entries());
    
    // Sort by last accessed time (oldest first)
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    // Remove oldest 20% of entries
    const toRemove = Math.floor(entries.length * 0.2);
    for (let i = 0; i < toRemove; i++) {
      this.delete(entries[i][0]);
    }
  }

  // Delete cache entry
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
    this.performanceMetrics.delete(key);
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.performanceMetrics.clear();
  }

  // Get cache statistics
  getStats() {
    const totalSize = Array.from(this.performanceMetrics.values())
      .reduce((sum, metrics) => sum + metrics.dataSize, 0);
    
    return {
      size: this.cache.size,
      totalDataSize: totalSize,
      entries: Array.from(this.performanceMetrics.entries()).map(([key, metrics]) => ({
        key,
        accessCount: metrics.accessCount,
        dataSize: metrics.dataSize,
        lastAccessed: new Date(metrics.lastAccessed).toISOString()
      }))
    };
  }

  // Preload data with background refresh
  async preload(key, fetchFn, ttl = this.defaultTTL) {
    // Return cached data immediately if available
    const cached = this.get(key);
    if (cached) {
      // Refresh in background if data is getting stale
      const expiry = this.timestamps.get(key);
      const timeUntilExpiry = expiry - Date.now();
      if (timeUntilExpiry < ttl * 0.2) { // If less than 20% TTL remaining
        this.refreshInBackground(key, fetchFn, ttl);
      }
      return cached;
    }
    
    // Fetch fresh data
    try {
      const data = await fetchFn();
      this.set(key, data, ttl);
      return data;
    } catch (error) {
      throw error;
    }
  }

  // Refresh data in background
  async refreshInBackground(key, fetchFn, ttl = this.defaultTTL) {
    try {
      const data = await fetchFn();
      this.set(key, data, ttl);
    } catch (error) {
    }
  }

  // Batch cache operations
  setBatch(entries) {
    entries.forEach(({ key, data, ttl }) => {
      this.set(key, data, ttl);
    });
  }

  getBatch(keys) {
    return keys.map(key => ({
      key,
      data: this.get(key)
    })).filter(entry => entry.data !== null);
  }
}

// Create singleton instance
const enhancedCacheService = new EnhancedCacheService();

export default enhancedCacheService;